import { useMemo, useState, type FormEvent } from "react";
import { CheckCircle2, Loader2, Sparkles, Wand2, X } from "lucide-react";
import type { PageSummary } from "@/hooks/use-pages";

export type CloneAdaptSuggestionKind = "headline" | "image" | "cta" | "countdown";

export interface CloneAdaptSuggestion {
  id: CloneAdaptSuggestionKind;
  label: string;
  description: string;
  preview: string;
}

export interface CloneAdaptSubmission {
  name: string;
  slug: string;
  suggestions: CloneAdaptSuggestion[];
}

interface PuckBlock {
  type: string;
  props?: Record<string, unknown>;
}

interface PuckData {
  content?: PuckBlock[];
  zones?: Record<string, PuckBlock[]>;
  root?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CloneAdaptWizardProps {
  page: PageSummary | null;
  isOpen: boolean;
  isSubmitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (submission: CloneAdaptSubmission) => Promise<void>;
}

const DEFAULT_SUGGESTIONS: CloneAdaptSuggestionKind[] = ["headline", "image", "cta", "countdown"];

const HEADLINE_KEYS = ["headline", "heading", "title", "text"];
const CTA_KEYS = ["ctaText", "buttonText", "submitLabel", "label"];
const IMAGE_KEYS = ["image", "imageUrl", "backgroundImage", "heroImage"];

export function slugifyCampaignName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

  return slug || "campaign-clone";
}

export function buildCloneAdaptSuggestions(campaignName: string): CloneAdaptSuggestion[] {
  const name = campaignName.trim() || "New Campaign";
  return [{
    id: "headline",
    label: "Rewrite the main headline",
    description: "Retarget the first headline-style field toward the new campaign promise.",
    preview: `Lead with "${name}" instead of the original generic headline.`,
  }, {
    id: "image",
    label: "Flag hero image for campaign swap",
    description: "Mark image-bearing hero or media blocks with a campaign-specific replacement brief.",
    preview: `Add an editor note to swap hero imagery for ${name}.`,
  }, {
    id: "cta",
    label: "Update primary CTA copy",
    description: "Change the first CTA-style field so the offer language matches the campaign.",
    preview: `Use "Claim ${name}" as the primary action.`,
  }, {
    id: "countdown",
    label: "Add urgency countdown",
    description: "Insert a countdown block below the hero area for time-boxed promotions.",
    preview: `Add a "${name} ends soon" countdown section.`,
  }];
}

export function applyCloneAdaptations(
  content: unknown,
  campaignName: string,
  suggestions: CloneAdaptSuggestion[],
): { content: unknown; changed: boolean } {
  const parsed = normalizePuckData(content);
  if (!parsed) {
    return { content, changed: false };
  }

  const selected = new Set(suggestions.map((suggestion) => suggestion.id));
  const next: PuckData = {
    ...parsed,
    content: parsed.content ? parsed.content.map(cloneBlock) : [],
    zones: parsed.zones ? cloneZones(parsed.zones) : undefined,
  };
  let changed = false;

  const blocks = listBlocks(next);

  if (selected.has("headline")) {
    changed = updateFirstStringProp(blocks, HEADLINE_KEYS, buildCampaignHeadline(campaignName)) || changed;
  }

  if (selected.has("cta")) {
    changed = updateFirstStringProp(blocks, CTA_KEYS, `Claim ${campaignName.trim() || "This Offer"}`) || changed;
  }

  if (selected.has("image")) {
    changed = flagFirstImageBlock(blocks, campaignName) || changed;
  }

  if (selected.has("countdown")) {
    const countdown = buildCountdownBlock(campaignName);
    next.content = [countdown, ...(next.content ?? [])];
    changed = true;
  }

  return { content: next, changed };
}

function normalizePuckData(content: unknown): PuckData | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return null;
  }

  const candidate = content as PuckData;
  if (candidate.content && !Array.isArray(candidate.content)) {
    return null;
  }

  return candidate;
}

function cloneBlock(block: PuckBlock): PuckBlock {
  return {
    ...block,
    props: block.props ? { ...block.props } : {},
  };
}

function cloneZones(zones: Record<string, PuckBlock[]>): Record<string, PuckBlock[]> {
  return Object.fromEntries(
    Object.entries(zones).map(([key, blocks]) => [key, blocks.map(cloneBlock)]),
  );
}

function listBlocks(data: PuckData): PuckBlock[] {
  return [
    ...(data.content ?? []),
    ...Object.values(data.zones ?? {}).flat(),
  ];
}

function updateFirstStringProp(blocks: PuckBlock[], keys: string[], value: string): boolean {
  for (const block of blocks) {
    if (!block.props) {
      continue;
    }

    const key = keys.find((candidate) => typeof block.props?.[candidate] === "string");
    if (!key) {
      continue;
    }

    block.props[key] = value;
    return true;
  }

  return false;
}

function flagFirstImageBlock(blocks: PuckBlock[], campaignName: string): boolean {
  for (const block of blocks) {
    if (!block.props) {
      continue;
    }

    const hasImageField = IMAGE_KEYS.some((key) => typeof block.props?.[key] === "string");
    if (!hasImageField && !/hero|image|media/i.test(block.type)) {
      continue;
    }

    block.props.campaignImageBrief = `Swap imagery to match ${campaignName.trim() || "the new campaign"}.`;
    return true;
  }

  return false;
}

function buildCampaignHeadline(campaignName: string): string {
  const name = campaignName.trim() || "New Campaign";
  return `${name}: built for the moment`;
}

function buildCountdownBlock(campaignName: string): PuckBlock {
  return {
    type: "CountdownTimer",
    props: {
      heading: `${campaignName.trim() || "Campaign"} ends soon`,
      deadline: "2026-12-31T23:59:59Z",
      expiredMessage: "This campaign has ended.",
      variant: "banner",
    },
  };
}

export function CloneAdaptWizard({
  page,
  isOpen,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: CloneAdaptWizardProps) {
  const [campaignName, setCampaignName] = useState("");
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<Set<CloneAdaptSuggestionKind>>(
    () => new Set(DEFAULT_SUGGESTIONS),
  );

  const suggestions = useMemo(() => buildCloneAdaptSuggestions(campaignName), [campaignName]);
  const slug = useMemo(() => slugifyCampaignName(campaignName), [campaignName]);

  if (!isOpen || !page) {
    return null;
  }

  const selectedSuggestions = suggestions.filter((suggestion) => selectedSuggestionIds.has(suggestion.id));

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!campaignName.trim() || selectedSuggestions.length === 0) {
      return;
    }

    await onSubmit({
      name: campaignName.trim(),
      slug,
      suggestions: selectedSuggestions,
    });
  }

  function toggleSuggestion(id: CloneAdaptSuggestionKind) {
    setSelectedSuggestionIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 px-4 py-8 backdrop-blur-sm">
      <form onSubmit={(event) => void handleSubmit(event)} className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[28px] border border-border bg-card shadow-2xl">
        <div className="border-b border-border bg-[radial-gradient(circle_at_top_left,var(--color-primary-100),transparent_42%),var(--color-card)] p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Clone and adapt
              </p>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
                Turn {page.title} into a campaign draft.
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Select the proposed changes before anything is applied. The wizard creates a private clone, saves an AI adaptation version, then opens the editor for final human review.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-full border border-border bg-background p-2 text-muted-foreground transition hover:bg-accent disabled:opacity-60"
              aria-label="Close clone and adapt wizard"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-6 p-6">
          <label className="block">
            <span className="text-sm font-medium text-foreground">What's this campaign for?</span>
            <input
              value={campaignName}
              onChange={(event) => setCampaignName(event.target.value)}
              placeholder="Valentine's dental whitening special"
              className="mt-2 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              autoFocus
            />
          </label>

          <div className="rounded-2xl border border-border bg-background p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">Clone slug</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">/{slug}</p>
              </div>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                {selectedSuggestions.length}/4 suggestions selected
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground">Review suggested adaptations</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                These are deterministic, explainable suggestions for this MVP. Richer AI recommendation generation can replace this source without changing the approval gate.
              </p>
            </div>

            {suggestions.map((suggestion) => {
              const isSelected = selectedSuggestionIds.has(suggestion.id);
              return (
                <button
                  key={suggestion.id}
                  type="button"
                  onClick={() => toggleSuggestion(suggestion.id)}
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    isSelected ? "border-primary/40 bg-primary/5" : "border-border bg-background hover:bg-accent"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 rounded-full border p-1 ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-foreground">{suggestion.label}</span>
                      <span className="mt-1 block text-sm leading-5 text-muted-foreground">{suggestion.description}</span>
                      <span className="mt-2 block rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground">{suggestion.preview}</span>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {error ? (
            <div className="rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-border bg-background/80 p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !campaignName.trim() || selectedSuggestions.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Clone, apply, open editor
          </button>
        </div>
      </form>
    </div>
  );
}
